import React from 'react';
import { Building2, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage({ setAuthPage }) {
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-300"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/70 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <button onClick={() => setAuthPage('landing')} className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Building2 size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">FlipperPro</h1>
            </div>
          </button>
          <button
            onClick={() => setAuthPage('landing')}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Back to Home
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-10">Last updated: March 1, 2026</p>

        <div className="space-y-8 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
            <p>
              FlipperPro ("we," "our," or "us") operates the FlipperPro web application (the
              "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard
              your information when you use our Service. By accessing or using the Service, you
              agree to the terms of this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>
            <p className="mb-3">We collect the following types of information:</p>
            <p className="mb-2">
              <span className="text-white font-medium">Account Information:</span> When you create
              an account, we collect your name, email address, and password. If you join a team, we
              collect your team role and team association.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Project Data:</span> Information you enter
              about your real estate projects, including property addresses, financial data
              (purchase prices, rehab costs, sale prices), task lists, expense records, photos,
              documents, and notes.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Third-Party Integrations:</span> If you
              connect your QuickBooks account, we receive access tokens and financial transaction
              data (expenses, bills, vendor information) from your QuickBooks account. We store
              OAuth tokens securely on our servers and never expose them to client-side code.
            </p>
            <p>
              <span className="text-white font-medium">Usage Data:</span> We may collect information
              about how you access and use the Service, including your browser type, access times,
              and pages viewed.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              3. How We Use Your Information
            </h2>
            <p className="mb-3">We use the information we collect to:</p>
            <p className="mb-1">- Provide, maintain, and improve the Service</p>
            <p className="mb-1">
              - Process and manage your real estate projects and financial calculations
            </p>
            <p className="mb-1">
              - Sync transaction data from connected third-party services like QuickBooks
            </p>
            <p className="mb-1">
              - Facilitate team collaboration and role-based access within your workspace
            </p>
            <p className="mb-1">- Send you notifications related to your account and projects</p>
            <p>- Respond to your comments, questions, and customer service requests</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Data Storage and Security</h2>
            <p className="mb-3">
              Your data is stored on Google Cloud Platform through Firebase, which provides
              enterprise-grade infrastructure with encryption at rest and in transit. We implement
              the following security measures:
            </p>
            <p className="mb-1">- All data is encrypted using TLS 1.2+ during transmission</p>
            <p className="mb-1">- Data at rest is encrypted using AES-256 encryption</p>
            <p className="mb-1">
              - Role-based access controls (RBAC) ensure team members only access authorized data
            </p>
            <p className="mb-1">- Each company's data is isolated in separate logical workspaces</p>
            <p className="mb-1">
              - OAuth tokens for third-party integrations are stored server-side using Firebase
              Cloud Functions secrets management, never in client-side code or browser storage
            </p>
            <p>- Firestore security rules enforce data ownership at the database level</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              5. QuickBooks Online Integration
            </h2>
            <p className="mb-3">
              FlipperPro integrates with Intuit QuickBooks Online ("QuickBooks") to help you manage
              your real estate project finances. This section describes in detail how we handle your
              QuickBooks data.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Data We Access from QuickBooks:</span> When
              you connect your QuickBooks account via OAuth 2.0, we request the{' '}
              <code className="text-blue-400 bg-slate-800 px-1.5 py-0.5 rounded text-sm">
                com.intuit.quickbooks.accounting
              </code>{' '}
              scope. This grants us read and write access to your QuickBooks company data,
              specifically: Purchases (expenses), Bills, Vendors, Accounts (chart of accounts), and
              Company Information (company name and email).
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">How We Use QuickBooks Data:</span> We use
              your QuickBooks data solely to:
            </p>
            <p className="mb-1">
              - Import and display your expenses and bills within your FlipperPro workspace
            </p>
            <p className="mb-1">
              - Allow you to assign QuickBooks transactions to your real estate projects
            </p>
            <p className="mb-1">
              - Push expenses created in FlipperPro back to QuickBooks as Purchase transactions
            </p>
            <p className="mb-1">
              - Map your FlipperPro expense categories to your QuickBooks chart of accounts
            </p>
            <p className="mb-3">- Display your QuickBooks company name and connection status</p>
            <p className="mb-2">
              <span className="text-white font-medium">QuickBooks Token Storage:</span> Your
              QuickBooks OAuth access tokens and refresh tokens are stored securely in our Firebase
              Firestore database using server-side Cloud Functions. Tokens are never exposed to
              client-side code, browser storage, or frontend JavaScript. Access tokens are
              automatically refreshed server-side before expiration. Token data is encrypted at rest
              via Google Cloud's AES-256 encryption and encrypted in transit via TLS 1.2+.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">QuickBooks Data Retention:</span> Synced
              QuickBooks transaction data (expenses, bills, vendor names) is cached in our database
              to provide a seamless experience. This cached data is refreshed each time you initiate
              a sync. When you disconnect your QuickBooks account, all stored OAuth tokens are
              immediately and permanently deleted, and cached QuickBooks transaction data is removed
              from your workspace.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Disconnecting QuickBooks:</span> You may
              disconnect your QuickBooks account at any time from your FlipperPro Expenses page.
              Upon disconnection, we immediately revoke your OAuth tokens and delete all stored
              QuickBooks credentials. No further API calls will be made to your QuickBooks account
              after disconnection.
            </p>
            <p>
              <span className="text-white font-medium">No Selling of QuickBooks Data:</span> We do
              not sell, license, or share your QuickBooks data with any third parties. Your
              QuickBooks data is used exclusively to provide the integration features described
              above within your FlipperPro workspace.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Other Third-Party Services</h2>
            <p className="mb-3">
              In addition to QuickBooks, our Service integrates with the following third-party
              services:
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Google Firebase:</span> We use Firebase for
              authentication, data storage, file storage, and cloud functions. Google's privacy
              policy applies to infrastructure-level data processing.
            </p>
            <p>
              <span className="text-white font-medium">Google Maps / Street View:</span> We use
              Google Maps APIs to display property images and location data. Google's privacy policy
              governs data collected through these APIs.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              7. Data Sharing and Disclosure
            </h2>
            <p className="mb-3">
              We do not sell, trade, or rent your personal information to third parties. We may
              share your information only in the following circumstances:
            </p>
            <p className="mb-1">
              - With your team members within your workspace, as configured by your team's access
              controls
            </p>
            <p className="mb-1">
              - With service providers who assist in operating our Service (e.g., Google Cloud
              Platform), bound by confidentiality obligations
            </p>
            <p className="mb-1">- If required by law, subpoena, or legal process</p>
            <p>- To protect our rights, property, or safety, or that of our users</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Data Retention</h2>
            <p>
              We retain your account data and project information for as long as your account is
              active. If you delete your account, we will delete your personal data within 30 days,
              except where we are required to retain it for legal or compliance purposes. Cached
              data from third-party integrations (such as QuickBooks transactions) is refreshed
              periodically and deleted upon disconnection.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Your Rights</h2>
            <p className="mb-3">You have the right to:</p>
            <p className="mb-1">
              - Access, correct, or delete your personal data at any time through your account
              settings
            </p>
            <p className="mb-1">- Export your project data</p>
            <p className="mb-1">
              - Disconnect any third-party integrations and have associated tokens deleted
              immediately
            </p>
            <p className="mb-1">- Request a full copy of your stored data by contacting us</p>
            <p>- Withdraw consent and close your account at any time</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              10. California Privacy Rights (CCPA)
            </h2>
            <p>
              If you are a California resident, you have additional rights under the California
              Consumer Privacy Act (CCPA), including the right to know what personal information we
              collect, the right to request deletion, and the right to opt out of the sale of
              personal information. We do not sell personal information. To exercise your CCPA
              rights, contact us at the email below.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Children's Privacy</h2>
            <p>
              Our Service is not intended for individuals under the age of 18. We do not knowingly
              collect personal information from children. If we become aware that we have collected
              data from a child, we will take steps to delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new Privacy Policy on this page and updating the "Last
              updated" date. Your continued use of the Service after changes are posted constitutes
              your acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your data rights,
              please contact us at:
            </p>
            <p className="mt-2 text-white font-medium">FlipperPro</p>
            <p>Email: privacy@flipperpro.com</p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
              <Building2 size={16} className="text-white" />
            </div>
            <span className="text-sm text-slate-500">
              &copy; 2025 FlipperPro. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <span className="text-white font-medium">Privacy</span>
            <button
              onClick={() => setAuthPage('terms')}
              className="hover:text-white transition-colors"
            >
              Terms
            </button>
            <button
              onClick={() => setAuthPage('landing')}
              className="hover:text-white transition-colors"
            >
              Home
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
